/**
 * GMB Opening Hours Utilities
 * Functions to process Google My Business opening hours data
 */

import { getISODay, isSameDay, addDays, isAfter, isBefore, startOfDay } from "date-fns";
import { DAYS_ORDER, ISO_DAY_TO_NAME, DAY_NAME_TO_ISO } from "@Src_main/types/openingHours";

/**
 * Format a TimeOfDay object to "HH:mm" string
 * @param {import('@Src_main/types/openingHours').TimeOfDay} timeObj
 * @returns {string}
 */
export function formatTime(timeObj) {
  if (!timeObj) return '';
  var hours = String(timeObj.hours || 0).padStart(2, '0');
  var minutes = String(timeObj.minutes || 0).padStart(2, '0');
  return "".concat(hours, ":").concat(minutes);
}

/**
 * Parse a "HH:mm" string to TimeOfDay object
 * @param {string} timeStr
 * @returns {import('@Src_main/types/openingHours').TimeOfDay}
 */
export function parseTime(timeStr) {
  var [hours, minutes] = timeStr.split(':').map(Number);
  return {
    hours,
    minutes: minutes || 0
  };
}

/**
 * Convert a DateObject to a JavaScript Date
 * @param {import('@Src_main/types/openingHours').DateObject} dateObj
 * @returns {Date}
 */
export function dateObjectToDate(dateObj) {
  return new Date(dateObj.year, dateObj.month - 1, dateObj.day);
}

/**
 * Convert a JavaScript Date to a DateObject
 * @param {Date} date
 * @returns {import('@Src_main/types/openingHours').DateObject}
 */
export function dateToDateObject(date) {
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate()
  };
}

/**
 * Check if two DateObjects represent the same day
 * @param {import('@Src_main/types/openingHours').DateObject} a
 * @param {import('@Src_main/types/openingHours').DateObject} b
 * @returns {boolean}
 */
export function isSameDateObject(a, b) {
  return a.year === b.year && a.month === b.month && a.day === b.day;
}

/**
 * Get the day name for a given date
 * @param {Date} date
 * @returns {import('@Src_main/types/openingHours').DayName}
 */
export function getDayName(date) {
  var isoDay = getISODay(date);
  return ISO_DAY_TO_NAME[isoDay];
}

/**
 * Get regular hours for a specific day from RegularHours
 * @param {import('@Src_main/types/openingHours').DayName} dayName
 * @param {import('@Src_main/types/openingHours').RegularHours} regularHours
 * @returns {import('@Src_main/types/openingHours').Period[]|null}
 */
export function getRegularHoursForDay(dayName, regularHours) {
  if (!(regularHours !== null && regularHours !== void 0 && regularHours.periods)) return null;
  var periodsForDay = regularHours.periods.filter(period => period.openDay === dayName);
  if (periodsForDay.length === 0) return null;
  return periodsForDay;
}

/**
 * Check if a date has special hours defined
 * @param {Date} date
 * @param {import('@Src_main/types/openingHours').SpecialHours|null} specialHours
 * @returns {import('@Src_main/types/openingHours').SpecialHourPeriod|null}
 */
export function getSpecialHoursForDate(date, specialHours) {
  if (!(specialHours !== null && specialHours !== void 0 && specialHours.specialHourPeriods)) return null;
  var targetDateObj = dateToDateObject(date);
  return specialHours.specialHourPeriods.find(period => isSameDateObject(period.startDate, targetDateObj)) || null;
}

/**
 * Get effective hours for a specific date (considers special hours)
 * @param {Date} date
 * @param {import('@Src_main/types/openingHours').RegularHours} regularHours
 * @param {import('@Src_main/types/openingHours').SpecialHours|null} specialHours
 * @returns {import('@Src_main/types/openingHours').EffectiveHours}
 */
export function getEffectiveHoursForDate(date, regularHours, specialHours) {
  var dayName = getDayName(date);
  var specialPeriod = getSpecialHoursForDate(date, specialHours);

  // If there's a special period for this date
  if (specialPeriod) {
    if (specialPeriod.closed) {
      return {
        type: 'closed',
        periods: [],
        isSpecialDay: true,
        reason: 'Chiusura straordinaria'
      };
    }

    // Special hours with modified opening times
    if (specialPeriod.openTime && specialPeriod.closeTime) {
      return {
        type: 'modified',
        periods: [{
          openDay: dayName,
          openTime: specialPeriod.openTime,
          closeDay: dayName,
          closeTime: specialPeriod.closeTime
        }],
        isSpecialDay: true,
        reason: 'Orario modificato'
      };
    }
  }

  // Use regular hours
  var regularPeriods = getRegularHoursForDay(dayName, regularHours);
  if (!regularPeriods || regularPeriods.length === 0) {
    return {
      type: 'closed',
      periods: [],
      isSpecialDay: false
    };
  }
  return {
    type: 'open',
    periods: regularPeriods,
    isSpecialDay: false
  };
}

/**
 * Check if a store is closed at a specific datetime
 * @param {Date} datetime
 * @param {import('@Src_main/types/openingHours').RegularHours} regularHours
 * @param {import('@Src_main/types/openingHours').SpecialHours|null} specialHours
 * @returns {boolean}
 */
export function isStoreClosedAt(datetime, regularHours, specialHours) {
  var effectiveHours = getEffectiveHoursForDate(datetime, regularHours, specialHours);
  if (effectiveHours.type === 'closed') {
    return true;
  }

  // Check if the time is within any open period
  var timeStr = formatTime({
    hours: datetime.getHours(),
    minutes: datetime.getMinutes()
  });
  return !effectiveHours.periods.some(period => {
    var openStr = formatTime(period.openTime);
    var closeStr = formatTime(period.closeTime);
    return timeStr >= openStr && timeStr < closeStr;
  });
}

/**
 * Check if a store is open at a specific datetime
 * @param {Date} datetime
 * @param {import('@Src_main/types/openingHours').RegularHours} regularHours
 * @param {import('@Src_main/types/openingHours').SpecialHours|null} specialHours
 * @returns {boolean}
 */
export function isStoreOpenAt(datetime, regularHours, specialHours) {
  return !isStoreClosedAt(datetime, regularHours, specialHours);
}

/**
 * Get upcoming special hours within the next N days
 * @param {import('@Src_main/types/openingHours').SpecialHours|null} specialHours
 * @param {number} daysAhead - Number of days to look ahead
 * @returns {import('@Src_main/types/openingHours').SpecialHourPeriod[]}
 */
export function getUpcomingSpecialHours(specialHours) {
  var daysAhead = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 30;
  if (!(specialHours !== null && specialHours !== void 0 && specialHours.specialHourPeriods)) return [];
  var today = startOfDay(new Date());
  var futureDate = addDays(today, daysAhead);
  return specialHours.specialHourPeriods.filter(period => {
    var periodDate = dateObjectToDate(period.startDate);
    return !isBefore(periodDate, today) && !isAfter(periodDate, futureDate);
  }).sort((a, b) => {
    var dateA = dateObjectToDate(a.startDate);
    var dateB = dateObjectToDate(b.startDate);
    return dateA - dateB;
  });
}

/**
 * Group consecutive special hours with the same status
 * @param {import('@Src_main/types/openingHours').SpecialHourPeriod[]} specialPeriods
 * @returns {import('@Src_main/types/openingHours').GroupedSpecialHours[]}
 */
export function groupConsecutiveSpecialHours(specialPeriods) {
  if (!specialPeriods || specialPeriods.length === 0) return [];
  var sorted = [...specialPeriods].sort((a, b) => {
    var dateA = dateObjectToDate(a.startDate);
    var dateB = dateObjectToDate(b.startDate);
    return dateA - dateB;
  });
  var groups = [];
  var currentGroup = null;
  sorted.forEach(period => {
    var periodDate = dateObjectToDate(period.startDate);
    var isClosed = !!period.closed;
    var openTime = period.openTime;
    var closeTime = period.closeTime;

    // Check if this can be added to the current group
    if (currentGroup) {
      var lastDate = dateObjectToDate(currentGroup.endDate);
      var nextDay = addDays(lastDate, 1);
      var isConsecutive = isSameDay(periodDate, nextDay);
      var sameStatus = currentGroup.closed === isClosed && (isClosed || formatTime(currentGroup.openTime) === formatTime(openTime) && formatTime(currentGroup.closeTime) === formatTime(closeTime));
      if (isConsecutive && sameStatus) {
        currentGroup.endDate = period.startDate;
        return;
      }
    }

    // Start a new group
    currentGroup = {
      startDate: period.startDate,
      endDate: period.startDate,
      closed: isClosed,
      openTime: openTime,
      closeTime: closeTime
    };
    groups.push(currentGroup);
  });
  return groups;
}

/**
 * Format periods for display (e.g., "12:00 - 15:00 / 19:00 - 23:00")
 * @param {import('@Src_main/types/openingHours').Period[]} periods
 * @returns {string}
 */
export function formatPeriods(periods) {
  if (!periods || periods.length === 0) return 'Chiuso';
  return periods.map(p => "".concat(formatTime(p.openTime), " - ").concat(formatTime(p.closeTime))).join(' / ');
}

/**
 * Convert GMB regular hours to the store_hours format used by the app
 * @param {import('@Src_main/types/openingHours').RegularHours} regularHours
 * @returns {Object} - Format: { 1: [{open, close}], 2: [...], ... }
 */
export function gmbHoursToStoreHours(regularHours) {
  if (!(regularHours !== null && regularHours !== void 0 && regularHours.periods)) return {};
  var result = {};
  regularHours.periods.forEach(period => {
    var dayNum = DAY_NAME_TO_ISO[period.openDay];
    if (!dayNum) return;
    if (!result[dayNum]) {
      result[dayNum] = [];
    }
    result[dayNum].push({
      open: formatTime(period.openTime),
      close: formatTime(period.closeTime)
    });
  });

  // Sort hours within each day
  Object.keys(result).forEach(day => {
    result[day].sort((a, b) => a.open.localeCompare(b.open));
  });
  return result;
}

/**
 * Check if a specific time slot is available based on GMB hours
 * @param {string} timestamp - ISO timestamp string "YYYY-MM-DDTHH:mm:ss"
 * @param {import('@Src_main/types/openingHours').RegularHours} regularHours
 * @param {import('@Src_main/types/openingHours').SpecialHours|null} specialHours
 * @returns {{ available: boolean, reason: import('@Src_main/types/openingHours').UnavailabilityReason }}
 */
export function checkSlotAvailabilityGMB(timestamp, regularHours, specialHours) {
  var slotDate = new Date(timestamp);
  var effectiveHours = getEffectiveHoursForDate(slotDate, regularHours, specialHours);

  // Check if the day is closed
  if (effectiveHours.type === 'closed') {
    return {
      available: false,
      reason: effectiveHours.isSpecialDay ? 'STORE_CLOSED_SPECIAL' : 'STORE_CLOSED_REGULAR'
    };
  }

  // Check if the time is within any open period
  var timeStr = formatTime({
    hours: slotDate.getHours(),
    minutes: slotDate.getMinutes()
  });
  var isWithinOpenPeriod = effectiveHours.periods.some(period => {
    var openStr = formatTime(period.openTime);
    var closeStr = formatTime(period.closeTime);
    return timeStr >= openStr && timeStr < closeStr;
  });
  if (!isWithinOpenPeriod) {
    return {
      available: false,
      reason: effectiveHours.isSpecialDay ? 'STORE_CLOSED_SPECIAL' : 'STORE_CLOSED_REGULAR'
    };
  }
  return {
    available: true,
    reason: null
  };
}

/**
 * Get status info for display (today/tomorrow hours, open status)
 * @param {import('@Src_main/types/openingHours').RegularHours} regularHours
 * @param {import('@Src_main/types/openingHours').SpecialHours|null} specialHours
 * @returns {{ isOpen: boolean, todayHours: EffectiveHours, tomorrowHours: EffectiveHours }}
 */
export function getGmbStoreStatus(regularHours, specialHours) {
  var now = new Date();
  var tomorrow = addDays(now, 1);
  var todayHours = getEffectiveHoursForDate(now, regularHours, specialHours);
  var tomorrowHours = getEffectiveHoursForDate(tomorrow, regularHours, specialHours);
  var isOpen = isStoreOpenAt(now, regularHours, specialHours);
  return {
    isOpen,
    todayHours,
    tomorrowHours
  };
}